import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import styles from './Admin.module.css';

interface Team {
  id: string;
  name: string;
  description: string;
}

interface User {
  id: string;
  userId: string;
  userName: string;
  email: string;
  role: string;
  position: string;
  team: Team | null;
}

const Admin: React.FC = () => {
  const { userRole, userTeamId, userId, userName: currentUserName } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  
  // 팀 생성 폼 상태
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDesc, setNewTeamDesc] = useState('');
  const [editingTeamId, setEditingTeamId] = useState<string | null>(null);

  // 사용자 추가/수정 폼 상태
  const [newUser, setNewUser] = useState({
    userId: '',
    password: '',
    userName: '',
    email: '',
    teamId: '',
    role: '팀원',
    position: '사원',
  });
  const [editingUserId, setEditingUserId] = useState<string | null>(null);

  const isAdmin = userRole === '관리자';

  const positions = ["이사", "상무", "팀장", "부장", "차장", "과장", "대리", "사원"];

  useEffect(() => {
    fetchTeams();
    fetchUsers();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await api.get('/teams');
      setTeams(response.data);
      // 초기 팀 선택 설정 (인원 추가 시 필요)
      if (response.data.length > 0 && !newUser.teamId) {
        setNewUser(prev => ({ ...prev, teamId: response.data[0].id }));
      }
    } catch (err) {
      console.error('Failed to fetch teams', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to fetch users', err);
    }
  };

  const handleCreateOrUpdateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTeamName) return;
    try {
      if (editingTeamId) {
        await api.post(`/teams/${editingTeamId}`, { name: newTeamName, description: newTeamDesc });
        setEditingTeamId(null);
        alert('팀이 수정되었습니다.');
      } else {
        await api.post('/teams', { name: newTeamName, description: newTeamDesc });
        alert('팀이 생성되었습니다.');
      }
      setNewTeamName('');
      setNewTeamDesc('');
      fetchTeams();
    } catch (err) {
      console.error('Failed to handle team', err);
      alert('팀 처리 실패');
    }
  };

  const handleEditTeam = (team: Team) => {
    setEditingTeamId(team.id);
    setNewTeamName(team.name);
    setNewTeamDesc(team.description);
  };

  const handleCancelEditTeam = () => {
    setEditingTeamId(null);
    setNewTeamName('');
    setNewTeamDesc('');
  };

  const handleCreateOrUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingUserId) {
        const updateData = { ...newUser };
        if (!updateData.password) delete (updateData as any).password;
        
        await api.post(`/users/${editingUserId}`, updateData);
        setEditingUserId(null);
        alert('인원 정보가 수정되었습니다.');
      } else {
        await api.post('/users', newUser);
        alert('인원이 추가되었습니다.');
      }
      setNewUser({
        userId: '',
        password: '',
        userName: '',
        email: '',
        teamId: newUser.teamId || (teams.length > 0 ? teams[0].id : ''),
        role: '팀원',
        position: '사원',
      });
      fetchUsers();
    } catch (err: any) {
      console.error('Failed to handle user', err);
      alert(err.response?.data?.message || '인원 처리 실패');
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUserId(user.id);
    setNewUser({
      userId: user.userId,
      password: '',
      userName: user.userName,
      email: user.email,
      teamId: user.team?.id || '',
      role: user.role,
      position: user.position || '사원',
    });
  };

  const handleCancelEditUser = () => {
    setEditingUserId(null);
    setNewUser({
      userId: '',
      password: '',
      userName: '',
      email: '',
      teamId: teams.length > 0 ? teams[0].id : '',
      role: '팀원',
      position: '사원',
    });
  };

  const handleDeleteTeam = async (id: string) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/teams/${id}`);
      fetchTeams();
    } catch (err) {
      console.error('Failed to delete team', err);
    }
  };

  return (
    <div className={styles.container}>
      <h1 style={{ marginBottom: '30px' }}>관리자 메뉴</h1>

      {/* 팀 관리 섹션 (관리자만 노출) */}
      {isAdmin && (
        <section className={styles.section}>
          <h2 className={styles.title}>팀 관리</h2>
          <form className={styles.form} onSubmit={handleCreateOrUpdateTeam}>
            <div className={styles.inputGroup}>
              <label>팀명</label>
              <input 
                className={styles.input} 
                value={newTeamName} 
                onChange={(e) => setNewTeamName(e.target.value)} 
                placeholder="예: 개발팀"
                required
              />
            </div>
            <div className={styles.inputGroup}>
              <label>설명</label>
              <input 
                className={styles.input} 
                value={newTeamDesc} 
                onChange={(e) => setNewTeamDesc(e.target.value)} 
                placeholder="간략한 설명"
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', alignSelf: 'flex-end' }}>
              <button type="submit" className={styles.submitBtn}>
                {editingTeamId ? '팀 수정' : '팀 생성'}
              </button>
              {editingTeamId && (
                <button type="button" className={styles.deleteBtn} onClick={handleCancelEditTeam}>취소</button>
              )}
            </div>
          </form>

          <table className={styles.table}>
            <thead>
              <tr>
                <th>팀명</th>
                <th>설명</th>
                <th>작업</th>
              </tr>
            </thead>
            <tbody>
              {teams.length === 0 && <tr><td colSpan={3} className={styles.emptyMsg}>등록된 팀이 없습니다.</td></tr>}
              {teams.map(team => (
                <tr key={team.id}>
                  <td>{team.name}</td>
                  <td>{team.description}</td>
                  <td>
                    <button 
                      className={styles.submitBtn} 
                      style={{ padding: '4px 8px', fontSize: '12px', marginRight: '5px' }}
                      onClick={() => handleEditTeam(team)}
                    >
                      수정
                    </button>
                    <button className={styles.deleteBtn} onClick={() => handleDeleteTeam(team.id)}>삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* 인원 관리 섹션 */}
      <section className={styles.section}>
        <h2 className={styles.title}>인원 관리</h2>
        <form className={styles.form} onSubmit={handleCreateOrUpdateUser}>
          <div className={styles.inputGroup}>
            <label>아이디</label>
            <input 
              className={styles.input} 
              value={newUser.userId} 
              onChange={(e) => setNewUser({...newUser, userId: e.target.value})} 
              required
              disabled={!!editingUserId}
            />
          </div>
          <div className={styles.inputGroup}>
            <label>이름</label>
            <input 
              className={styles.input} 
              value={newUser.userName} 
              onChange={(e) => setNewUser({...newUser, userName: e.target.value})} 
              required
            />
          </div>
          <div className={styles.inputGroup}>
            <label>비밀번호</label>
            <input 
              type="password"
              className={styles.input} 
              value={newUser.password} 
              onChange={(e) => setNewUser({...newUser, password: e.target.value})} 
              placeholder={editingUserId ? "변경 시에만 입력" : "초기 비밀번호"}
              required={!editingUserId}
            />
          </div>
          <div className={styles.inputGroup}>
            <label>이메일</label>
            <input 
              type="email"
              className={styles.input} 
              value={newUser.email} 
              onChange={(e) => setNewUser({...newUser, email: e.target.value})} 
              required
            />
          </div>
          <div className={styles.inputGroup}>
            <label>소속 팀</label>
            <select 
              className={styles.select}
              value={newUser.teamId}
              onChange={(e) => setNewUser({...newUser, teamId: e.target.value})}
              disabled={!isAdmin}
            >
              {teams.map(team => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          </div>
          <div className={styles.inputGroup}>
            <label>역할</label>
            <select 
              className={styles.select}
              value={newUser.role}
              onChange={(e) => setNewUser({...newUser, role: e.target.value})}
            >
              <option value="팀원">팀원</option>
              <option value="파트장">파트장</option>
              {isAdmin && <option value="관리자">관리자</option>}
            </select>
          </div>
          <div className={styles.inputGroup}>
            <label>직급</label>
            <select 
              className={styles.select}
              value={newUser.position}
              onChange={(e) => setNewUser({...newUser, position: e.target.value})}
            >
              {positions.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignSelf: 'flex-end' }}>
            <button type="submit" className={styles.submitBtn}>
              {editingUserId ? '인원 수정' : '인원 추가'}
            </button>
            {editingUserId && (
              <button type="button" className={styles.deleteBtn} onClick={handleCancelEditUser}>취소</button>
            )}
          </div>
        </form>

        <table className={styles.table}>
          <thead>
            <tr>
              <th>아이디</th>
              <th>이름</th>
              <th>직급</th>
              <th>이메일</th>
              <th>소속 팀</th>
              <th>역할</th>
              <th>작업</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && <tr><td colSpan={7} className={styles.emptyMsg}>등록된 인원이 없습니다.</td></tr>}
            {users.map(user => (
              <tr key={user.id}>
                <td>{user.userId}</td>
                <td>{user.userName}</td>
                <td>{user.position || '사원'}</td>
                <td>{user.email}</td>
                <td>{user.team?.name || '소속 없음'}</td>
                <td>{user.role}</td>
                <td>
                  <button 
                    className={styles.submitBtn} 
                    style={{ padding: '4px 8px', fontSize: '12px' }}
                    onClick={() => handleEditUser(user)}
                  >
                    수정
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default Admin;
